import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  Timestamp,
  onSnapshot,
  serverTimestamp,
  writeBatch,
  type Unsubscribe,
  type FieldValue,
} from 'firebase/firestore';
import { db } from './firebase';
import { deleteAllPhotos, deletePhotoFile } from './photos';
import { ownedStoragePath, storagePathFromDownloadURL } from './storage';
import type { Plant, CreatePlantInput, UpdatePlantInput, AnyCareInfo } from '../types/plant';

/**
 * Get all plants for a user, ordered by creation date (newest first)
 */
export async function getPlants(userId: string): Promise<Plant[]> {
  const plantsRef = collection(db, 'users', userId, 'plants');
  const q = query(plantsRef, orderBy('createdAt', 'desc'));

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as Plant[];
}

/**
 * Get a single plant by ID
 */
export async function getPlant(userId: string, plantId: string): Promise<Plant | null> {
  const plantRef = doc(db, 'users', userId, 'plants', plantId);
  const snapshot = await getDoc(plantRef);

  if (!snapshot.exists()) {
    return null;
  }

  return {
    id: snapshot.id,
    ...snapshot.data(),
  } as Plant;
}

/**
 * Get the count of plants for a user
 */
export async function getPlantCount(userId: string): Promise<number> {
  const plantsRef = collection(db, 'users', userId, 'plants');
  const snapshot = await getDocs(plantsRef);
  return snapshot.size;
}

/**
 * Subscribe to plants for a user (real-time updates)
 * Alias: subscribePlants
 */
export function subscribeToPlants(
  userId: string,
  callback: (plants: Plant[]) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  const plantsRef = collection(db, 'users', userId, 'plants');
  const q = query(plantsRef, orderBy('createdAt', 'desc'));

  return onSnapshot(
    q,
    (snapshot) => {
      const plants = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as Plant[];
      callback(plants);
    },
    (error) => {
      console.error('Plants subscription error:', error);
      onError?.(error);
    }
  );
}

// Alias for subscribeToPlants (some hooks use this name)
export const subscribePlants = subscribeToPlants;

/**
 * Subscribe to a single plant (real-time updates)
 */
export function subscribeToPlant(
  userId: string,
  plantId: string,
  callback: (plant: Plant | null) => void,
  onError?: (error: Error) => void
): Unsubscribe {
  const plantRef = doc(db, 'users', userId, 'plants', plantId);

  return onSnapshot(
    plantRef,
    (snapshot) => {
      if (!snapshot.exists()) {
        callback(null);
        return;
      }
      callback({
        id: snapshot.id,
        ...snapshot.data(),
      } as Plant);
    },
    (error) => {
      console.error('Plant subscription error:', error);
      onError?.(error);
    }
  );
}

/**
 * Add a new plant
 */
export async function addPlant(
  userId: string,
  input: CreatePlantInput
): Promise<{ success: true; id: string } | { success: false; error: string }> {
  try {
    const plantsRef = collection(db, 'users', userId, 'plants');

    const plantData = {
      commonName: input.commonName,
      scientificName: input.scientificName ?? null,
      nickname: input.nickname ?? null,
      photoURL: input.photoURL,
      photoStoragePath: input.photoStoragePath ?? null,
      careInfo: input.careInfo ?? null,
      location: input.location ?? null,
      acquiredDate: input.acquiredDate ? Timestamp.fromDate(input.acquiredDate) : null,
      lastWatered: null,
      isCustom: input.isCustom ?? false,
      sortOrder: null,
      wateringIntervalDays: null,
      wateringAdjustmentDays: 0,
      autoAdjust: true,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    const docRef = await addDoc(plantsRef, plantData);
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('Failed to add plant:', error);
    return { success: false, error: 'Could not save plant. Please try again.' };
  }
}

/**
 * Update an existing plant
 */
export async function updatePlant(
  userId: string,
  plantId: string,
  input: UpdatePlantInput
): Promise<{ success: true } | { success: false; error: string }> {
  try {
    const plantRef = doc(db, 'users', userId, 'plants', plantId);

    type UpdateValue = string | number | boolean | null | AnyCareInfo | Timestamp | FieldValue;
    const updateData: Record<string, UpdateValue> = {
      updatedAt: serverTimestamp(),
    };

    if (input.commonName !== undefined) updateData.commonName = input.commonName;
    if (input.scientificName !== undefined) updateData.scientificName = input.scientificName;
    if (input.nickname !== undefined) updateData.nickname = input.nickname;
    if (input.photoURL !== undefined) updateData.photoURL = input.photoURL;
    if (input.careInfo !== undefined) updateData.careInfo = input.careInfo;
    if (input.location !== undefined) updateData.location = input.location;
    if (input.acquiredDate !== undefined) {
      updateData.acquiredDate = input.acquiredDate ? Timestamp.fromDate(input.acquiredDate) : null;
    }
    if (input.lastWatered !== undefined) {
      updateData.lastWatered = input.lastWatered ? Timestamp.fromDate(input.lastWatered) : null;
    }
    if (input.wateringIntervalDays !== undefined) updateData.wateringIntervalDays = input.wateringIntervalDays;
    if (input.wateringAdjustmentDays !== undefined) updateData.wateringAdjustmentDays = input.wateringAdjustmentDays;
    if (input.autoAdjust !== undefined) updateData.autoAdjust = input.autoAdjust;

    await updateDoc(plantRef, updateData);
    return { success: true };
  } catch (error) {
    console.error('Failed to update plant:', error);
    return { success: false, error: 'Could not update plant. Please try again.' };
  }
}

/**
 * Update a plant's care info
 */
export async function updatePlantCareInfo(
  userId: string,
  plantId: string,
  careInfo: AnyCareInfo
): Promise<{ success: true } | { success: false; error: string }> {
  try {
    const plantRef = doc(db, 'users', userId, 'plants', plantId);
    await updateDoc(plantRef, {
      careInfo,
      updatedAt: serverTimestamp(),
    });
    return { success: true };
  } catch (error) {
    console.error('Failed to update care info:', error);
    return { success: false, error: 'Could not update care info. Please try again.' };
  }
}

/**
 * Update a plant's care info generation status (for UI loading states)
 */
export async function updatePlantCareInfoStatus(
  userId: string,
  plantId: string,
  status: 'pending' | 'generating' | 'complete' | 'error',
  errorMessage?: string
): Promise<{ success: true } | { success: false; error: string }> {
  try {
    const plantRef = doc(db, 'users', userId, 'plants', plantId);
    type StatusUpdateValue = string | FieldValue;
    const updateData: Record<string, StatusUpdateValue> = {
      careInfoStatus: status,
      updatedAt: serverTimestamp(),
    };
    if (errorMessage !== undefined) {
      updateData.careInfoError = errorMessage;
    }
    await updateDoc(plantRef, updateData);
    return { success: true };
  } catch (error) {
    console.error('Failed to update care info status:', error);
    return { success: false, error: 'Could not update care info status.' };
  }
}

/**
 * Mark a plant as watered (updates lastWatered to now)
 */
export async function waterPlant(
  userId: string,
  plantId: string
): Promise<{ success: true } | { success: false; error: string }> {
  try {
    const plantRef = doc(db, 'users', userId, 'plants', plantId);
    await updateDoc(plantRef, {
      lastWatered: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return { success: true };
  } catch (error) {
    console.error('Failed to water plant:', error);
    return { success: false, error: 'Could not record watering. Please try again.' };
  }
}

/**
 * Undo a water action by restoring the previous lastWatered value
 */
export async function undoWaterPlant(
  userId: string,
  plantId: string,
  previousLastWatered: Timestamp | null
): Promise<{ success: true } | { success: false; error: string }> {
  try {
    const plantRef = doc(db, 'users', userId, 'plants', plantId);
    await updateDoc(plantRef, {
      lastWatered: previousLastWatered,
      updatedAt: serverTimestamp(),
    });
    return { success: true };
  } catch (error) {
    console.error('Failed to undo water:', error);
    return { success: false, error: 'Could not undo watering. Please try again.' };
  }
}

/**
 * Resolve the Storage object backing a plant's `photoURL`, or null if there
 * isn't one we may delete.
 *
 * Two sources, because plants predate the field: the path recorded at upload
 * time, falling back to recovering it from the download URL. Both go through
 * the ownership guard — Firestore rules let a client write any value into
 * `photoStoragePath`, so trusting it just because we usually write it would
 * leave the shared `generated-reference/` cache deletable.
 *
 * Returns null for reference photos (Unsplash, Wikimedia, AI-generated) and the
 * local placeholder. Those are not ours, and a delete attempt on them is a bug,
 * not a no-op.
 */
function plantPhotoStoragePath(userId: string, plant: Plant | null): string | null {
  if (!plant) return null;

  return (
    ownedStoragePath(userId, plant.photoStoragePath) ??
    ownedStoragePath(userId, storagePathFromDownloadURL(plant.photoURL))
  );
}

/**
 * Delete a plant and all associated data (photos, diagnoses)
 */
export async function deletePlant(
  userId: string,
  plantId: string
): Promise<{ success: true } | { success: false; error: string }> {
  try {
    // Get plant data first to find photo URL
    const plant = await getPlant(userId, plantId);

    // Cascade the photo gallery before the plant doc goes away — once the
    // parent is deleted the subcollection is unreachable from the client and
    // its Storage objects would be orphaned.
    await deleteAllPhotos(userId, plantId);

    // Delete the plant document
    const plantRef = doc(db, 'users', userId, 'plants', plantId);
    await deleteDoc(plantRef);

    // Then the plant's own photo — the one uploaded at add-time, which predates
    // the gallery and so isn't covered by deleteAllPhotos above.
    await deletePhotoFile(plantPhotoStoragePath(userId, plant));

    // Note: Diagnosis subcollection cleanup could be handled by a Cloud Function trigger
    // For now, orphaned diagnoses will be cleaned up by the scheduled cleanup function
    return { success: true };
  } catch (error) {
    console.error('Failed to delete plant:', error);
    return { success: false, error: 'Could not delete plant. Please try again.' };
  }
}

/**
 * Batch-update sortOrder for multiple plants.
 */
export async function updatePlantSortOrders(
  userId: string,
  orders: Array<{ id: string; sortOrder: number }>
): Promise<{ success: true } | { success: false; error: string }> {
  try {
    const batch = writeBatch(db);
    for (const { id, sortOrder } of orders) {
      const plantRef = doc(db, 'users', userId, 'plants', id);
      batch.update(plantRef, { sortOrder, updatedAt: serverTimestamp() });
    }
    await batch.commit();
    return { success: true };
  } catch (error) {
    console.error('Failed to update sort orders:', error);
    return { success: false, error: 'Could not save plant order. Please try again.' };
  }
}

/**
 * Format lastWatered timestamp to ISO string or null
 */
export function formatLastWatered(lastWatered: Timestamp | null): string | null {
  if (!lastWatered) return null;
  return lastWatered.toDate().toISOString();
}
